import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "rec-recovery" }
const _hoisted_2 = { class: "rr-warn" }
const _hoisted_3 = { class: "rr-info" }
const _hoisted_4 = { class: "rr-row" }
const _hoisted_5 = { class: "rr-row" }
const _hoisted_6 = ["title"]
const _hoisted_7 = {
  key: 0,
  class: "rr-row"
}
const _hoisted_8 = { class: "rr-actions" }

import {ref, computed, onMounted, watch} from 'vue'
import {notification, Modal} from 'ant-design-vue'
import {DownloadOutlined, CloudUploadOutlined} from '@ant-design/icons-vue'
import {useAuthStore} from '@/stores'
import {getSessionMeta, getChunkCount, assembleBlob, clearSession, RecorderSessionMeta} from '@/utils/recorderStore'
import TaskService from '@/services/task'


export default /*@__PURE__*/_defineComponent({
  __name: 'RecordingRecovery',
  setup(__props) {

const visible = ref(false)
const meta = ref<RecorderSessionMeta | null>(null)
const downloading = ref(false)
const uploading = ref(false)

const authStore = useAuthStore()

const durationLabel = computed(() => {
    const s = meta.value?.durationSec
    if (!s) return ''
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = s % 60
    const pad = (n: number) => n.toString().padStart(2, '0')
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`
})

/**
 * Check IndexedDB for any pending recording and show the recovery modal.
 * Called on mount and again whenever the user successfully re-authenticates,
 * so that a recording captured before a session expiry can still be uploaded.
 */
const checkAndShow = async () => {
    try {
        const m = await getSessionMeta()
        if (!m) return
        const count = await getChunkCount()
        if (count > 0) {
            meta.value = m
            visible.value = true
        } else {
            // No data — clear stale metadata.
            await clearSession()
        }
    } catch (e) {
        console.warn('[RecordingRecovery] check failed:', e)
    }
}

onMounted(checkAndShow)

// Re-check when the user logs back in after a TOKEN_INVALID dismissal.
// isLoggedIn goes from false → true when AuthStore restores a new token.
watch(
    () => authStore.isLoggedIn,
    (loggedIn) => {
        if (loggedIn && !visible.value) {
            checkAndShow()
        }
    }
)

const buildBlob = async (): Promise<Blob | null> => {
    if (!meta.value) return null
    return assembleBlob(meta.value.mimeType)
}

/**
 * Bản dùng để GỬI LÊN: ưu tiên luồng chỉ-tiếng nếu phiên ghi màn hình có lưu.
 * Tải về vẫn là video đầy đủ, chỉ bản upload mới bỏ hình — engine chỉ bóc băng
 * âm thanh, mà phần hình từng làm upload vượt giới hạn 413 của nginx.
 */
const buildUploadBlob = async () => {
    if (!meta.value) return null
    if (meta.value.audioMimeType && (await getChunkCount('audio')) > 0) {
        return assembleBlob(meta.value.audioMimeType, 'audio')
    }
    return assembleBlob(meta.value.mimeType)
}

const onDownload = async () => {
    if (!meta.value || downloading.value) return
    downloading.value = true
    try {
        const blob = await buildBlob()
        if (!blob || blob.size === 0) {
            notification.error({message: 'Lỗi', description: 'Không đọc được dữ liệu bản ghi.'})
            return
        }
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = meta.value.filename || 'recording.webm'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(url), 4000)
        notification.success({message: 'Đã tải về', description: 'File ghi đã được lưu về máy bạn.'})
        await clearSession()
        visible.value = false
    } catch (e) {
        console.error(e)
        notification.error({message: 'Lỗi', description: 'Không tải được file ghi.'})
    } finally {
        downloading.value = false
    }
}

const onUpload = async () => {
    if (!meta.value || uploading.value) return
    uploading.value = true
    try {
        const blob = await buildUploadBlob()
        if (!blob || blob.size === 0) {
            notification.error({message: 'Lỗi', description: 'Không đọc được dữ liệu bản ghi.'})
            return
        }
        const useAudio = !!meta.value.audioMimeType && blob.type === meta.value.audioMimeType
        const file = new File(
            [blob],
            (useAudio ? meta.value.audioFilename : meta.value.filename) || 'recording.webm',
            {type: blob.type || meta.value.mimeType}
        )
        const res: any = await TaskService.uploadTask('meeting', 'vc', null, file)
        if (res?.status) {
            notification.success({message: 'Thành công', description: 'Đã tải bản ghi lên hệ thống.'})
            await clearSession()
            visible.value = false
        } else {
            notification.error({message: 'Lỗi', description: res?.statusText || 'Tải lên thất bại. Bản ghi vẫn được giữ lại.'})
        }
    } catch (e) {
        console.error(e)
        notification.error({message: 'Lỗi', description: 'Không tải lên được. Bản ghi vẫn được giữ lại — bạn có thể tải về máy.'})
    } finally {
        uploading.value = false
    }
}

const onDiscard = () => {
    Modal.confirm({
        title: 'Xoá bản ghi?',
        content: 'Bản ghi tạm sẽ bị xoá vĩnh viễn và không thể khôi phục.',
        okText: 'Xoá',
        okType: 'danger',
        cancelText: 'Huỷ',
        onOk: async () => {
            await clearSession()
            visible.value = false
        },
    })
}

return (_ctx: any,_cache: any) => {
  const _component_a_button = _resolveComponent("a-button")!
  const _component_a_modal = _resolveComponent("a-modal")!

  return (_openBlock(), _createBlock(_component_a_modal, {
    open: visible.value,
    "onUpdate:open": _cache[0] || (_cache[0] = ($event: any) => ((visible).value = $event)),
    closable: false,
    "mask-closable": false,
    keyboard: false,
    footer: null,
    title: "⚠️ Khôi phục bản ghi chưa lưu",
    width: "440px"
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("p", _hoisted_2, [
          (meta.value?.status === 'recording')
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _cache[1] || (_cache[1] = _createTextVNode(" Quá trình ghi trước đó đã ", -1)),
                _cache[2] || (_cache[2] = _createElementVNode("b", null, "bị dừng đột ngột", -1)),
                _cache[3] || (_cache[3] = _createTextVNode(" — có thể do tab bị đóng, hoặc trình duyệt tự giải phóng bộ nhớ vì ", -1)),
                _cache[4] || (_cache[4] = _createElementVNode("b", null, "ghi quá lâu", -1)),
                _cache[5] || (_cache[5] = _createTextVNode(". Bản ghi đã được lưu tạm trên máy bạn; hãy tải về ngay để không bị mất. ", -1))
              ], 64))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _cache[6] || (_cache[6] = _createTextVNode(" Bạn có một bản ghi ", -1)),
                _cache[7] || (_cache[7] = _createElementVNode("b", null, "chưa được tải lên", -1)),
                _cache[8] || (_cache[8] = _createTextVNode(". Hãy tải về hoặc tải lên hệ thống. ", -1))
              ], 64))
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _cache[9] || (_cache[9] = _createElementVNode("span", null, "Loại", -1)),
            _createElementVNode("b", null, _toDisplayString(meta.value?.type === 'screen' ? 'Ghi màn hình' : 'Ghi âm'), 1)
          ]),
          _createElementVNode("div", _hoisted_5, [
            _cache[10] || (_cache[10] = _createElementVNode("span", null, "Tên file", -1)),
            _createElementVNode("b", {
              class: "rr-file",
              title: meta.value?.filename
            }, _toDisplayString(meta.value?.filename), 9, _hoisted_6)
          ]),
          (durationLabel.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _cache[11] || (_cache[11] = _createElementVNode("span", null, "Thời lượng", -1)),
                _createElementVNode("b", null, _toDisplayString(durationLabel.value), 1)
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_8, [
          _createVNode(_component_a_button, {
            type: "primary",
            loading: downloading.value,
            onClick: onDownload
          }, {
            icon: _withCtx(() => [
              _createVNode(_unref(DownloadOutlined))
            ]),
            default: _withCtx(() => [
              _cache[12] || (_cache[12] = _createTextVNode(" Tải về máy ", -1))
            ]),
            _: 1
          }, 8, ["loading"]),
          _createVNode(_component_a_button, {
            loading: uploading.value,
            onClick: onUpload
          }, {
            icon: _withCtx(() => [
              _createVNode(_unref(CloudUploadOutlined))
            ]),
            default: _withCtx(() => [
              _cache[13] || (_cache[13] = _createTextVNode(" Tải lên hệ thống ", -1))
            ]),
            _: 1
          }, 8, ["loading"]),
          _createVNode(_component_a_button, {
            danger: "",
            type: "text",
            disabled: downloading.value || uploading.value,
            onClick: onDiscard
          }, {
            default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
              _createTextVNode(" Xoá bản ghi ", -1)
            ]))]),
            _: 1
          }, 8, ["disabled"])
        ])
      ])
    ]),
    _: 1
  }, 8, ["open"]))
}
}

})